import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiInstance from '@/utils/api'
import {
  useOrderModes,
  type TimeSlot,
  type Mode1Settings,
  type Mode2Settings,
  type OrderModesData,
  type ReceptionAccountOption,
  type PriceLevelOption,
} from './modules/orderModes'
import {
  useReceptionAccounts,
  type ReceptionAccount,
} from './modules/receptionAccounts'
import {
  usePickupMonitors,
  type PickupMonitor,
  type PickupMonitorDraft,
  type MediaFile,
} from './modules/pickupMonitors'
import {
  useSOTAccounts,
  type SOTAccount,
  type SOTAccountDraft,
  type StartupMedia,
} from './modules/sotAccounts'
import {
  useSettings,
  type RestaurantSettingsData,
  type SettingsDraft,
} from './modules/settings'
import {
  useServiceAreas,
  type ServiceArea,
  type ServiceAreaDraft,
  type ServiceAreaSettings,
} from './modules/serviceAreas'
import {
  useArticles,
  type Article,
  type ArticleDraft,
  type TaxSettings,
  type TimeSlot,
  type LocalizedContent,
} from './modules/articles'
import {
  useBulkEdit,
} from './modules/bulkEdit'
import {
  useOfferingTimePresets,
  type OfferingTimePreset,
} from './modules/offeringTimePresets'
import {
  usePriceLevels,
  type PriceLevel,
} from './modules/priceLevels'
import {
  useMenuLanguages,
  type MenuLanguage,
} from './modules/menuLanguages'
import {
  useCloudPrinters,
  type CloudPrinter,
  type Category,
} from './modules/cloudPrinter'
import {
  useMenuCategories,
  type MenuCategoryNode,
} from './modules/menuCategories'
import {
  useFoodCategories,
  type FoodCategory,
  type LocalizedFoodCategoryContent,
} from './modules/foodCategories'
import {
  useFoodAttributes,
  type FoodAttribute,
  type LocalizedAttributeContent,
} from './modules/foodAttributes'
import {
  useFoodItemOptions,
  type OptionGroup,
  type OptionItem,
} from './modules/foodItemOptions'
import {
  useOrders,
  type OrderTransferred,
  type OrderActions,
  type OrderListItem,
  type OrdersListResponse,
  type OrdersFilter,
} from './modules/orders'

// ===== RE-EXPORT ORDERS INTERFACES =====
export type {
  OrderTransferred,
  OrderActions,
  OrderListItem,
  OrdersListResponse,
  OrdersFilter,
  OrderErrorSummaryItem,
  Payment,
  PaymentActions,
  Card,
} from './modules/orders'

// ===== BASIC INFORMATION INTERFACES =====
export interface BasicInformationData {
  restaurantName: string
  street: string
  city: string
  zipcode: string
  /** Read-only: maintained on the company, returned by the restaurant-info API */
  currency: string
  /** Restaurant setting "Do not show currency" — when true, prices render without a symbol */
  hideCurrency: boolean
  bannerImage: string
  partnerId: string
  companyId: string
  managerName: string
  managerEmail: string
  managerMobileNo: string
  urlSuffix: string
  defaultLanguage: 'en' | 'de'
  bannerChanged?: boolean
  hasOrder?: boolean
  /** Read-only here: changed through POST /restaurant/{id}/status, not the basic-info save */
  isActive?: boolean
}

// ===== PAYMENT GATEWAY INTERFACES =====
// One credential set per gateway is used for every restaurant now — use_sandbox_mode
// (Settings tab, admin-only) decides which endpoint they're sent to, not which column/field
// set is read. A sandbox restaurant's sandbox credentials go into these same fields.
export interface PaymentGatewayData {
  adyenConfig: {
    merchantAccount: string
    apiKey: string
    companyAccount: string
    clientKey: string
    hmacKey: string
    apiUriPrefix: string
    googlePayMerchantId: string
  }
  vrPayConfig: {
    merchantId: string
    blowfishPassword: string
    hmacPassword: string
  }
  activePaymentGateway: string
  useSandboxMode: boolean
  vatRate1: string
  vatRate2: string
}

// ===== OPENING HOURS INTERFACES =====
export interface OpeningHoursData {
  Monday: TimeSlot[]
  Tuesday: TimeSlot[]
  Wednesday: TimeSlot[]
  Thursday: TimeSlot[]
  Friday: TimeSlot[]
  Saturday: TimeSlot[]
  Sunday: TimeSlot[]
  closingDays: string[]
}

// ===== MATRIX INTERFACE INTERFACES =====
export interface MenuOption {
  value: string
  label: string
  selected: boolean
}

export interface OrderMode {
  mode_id: number
  mode_label: string
  options: MenuOption[]
  rest_data_id: number
  order_mode_name: string
  can_import: boolean
}

export interface MenuImportConfig {
  orderMode: string
  selectedMenu: string
  imported: boolean
}

export interface MenuCardAssignment {
  mode_id: number
  menu_card_id: string
  menu_card_name: string
}

export interface NeoServiceConfig {
  endpoint: string
  articleTitleFrom: 'name' | 'displayText' | 'printerText'
  dontUpdateTitle: boolean
  dontUpdateDescription: boolean
  preserveCategories: boolean
  autoSync: boolean
  assignMenuCards: boolean
  menuCardAssignments: MenuCardAssignment[]
  menus: MenuImportConfig[]
  modes?: OrderMode[]
}

export interface UniversalPosMode {
  terminalSerialNo: string
  waiterId: string
  waiterPassword: string
  bookToTableNumber?: string
  tableNumber?: string
}

export interface UniversalPosConfig {
  endpoint: string
  settlementTypeId: string
  allowCustomerInvoice: boolean
  httpServerPrefix: string
  mode1: UniversalPosMode
  mode2: UniversalPosMode
}

export interface MatrixInterfaceData {
  // null = no choice made yet; left to the admin to decide on the Matrix
  // Interface step, never defaulted or forced by the app.
  articleSetupMethod: 'import' | 'manual' | null
  neoService: NeoServiceConfig
  universalPos: UniversalPosConfig
  isPremium: boolean
  // Once a choice has been saved, the backend rejects switching it — the setup
  // method radios are hidden rather than left editable against a 403.
  isLocked: boolean
  // False for restaurants with no real (non-Flixorder) reseller partner, who may
  // only ever use 'manual' — mirrors the backend's restaurantHasRealPartner check.
  allowMatrixPos: boolean
}

// ===== RESTAURANT SETTINGS INTERFACES =====
// Module interfaces are re-exported from modules/orderModes.ts, modules/receptionAccounts.ts, modules/pickupMonitors.ts, and modules/settings.ts
export type {
  RestaurantSettingsData,
  SettingsDraft,
} from './modules/settings'

export type {
  Mode1Settings,
  Mode2Settings,
  OrderModesData,
  ReceptionAccountOption,
  PriceLevelOption,
  ReceptionAccount,
  ServiceArea,
  ServiceAreaDraft,
  ServiceAreaSettings,
  Article,
  ArticleDraft,
  TaxSettings,
  TimeSlot,
  LocalizedContent,
  PriceLevel,
  OfferingTimePreset,
  CloudPrinter,
  Category,
  MenuLanguage,
  OptionGroup,
  OptionItem,
}

// ===== HELPER FUNCTIONS =====
const initializeBasicInformationData = (): BasicInformationData => {
  return {
    restaurantName: '',
    street: '',
    city: '',
    zipcode: '',
    currency: '',
    hideCurrency: false,
    bannerImage: '/flixorder-banner.jpg',
    partnerId: '',
    companyId: '',
    managerName: '',
    managerEmail: '',
    managerMobileNo: '',
    urlSuffix: '',
    defaultLanguage: 'de',
  }
}

const initializePaymentGatewayData = (): PaymentGatewayData => {
  return {
    adyenConfig: {
      merchantAccount: '',
      apiKey: '',
      companyAccount: '',
      clientKey: '',
      hmacKey: '',
      apiUriPrefix: '',
      googlePayMerchantId: '',
    },
    vrPayConfig: {
      merchantId: '',
      blowfishPassword: '',
      hmacPassword: '',
    },
    activePaymentGateway: 'adyen',
    // Every restaurant starts on Live; Sandbox is switched on separately, admin-only, via
    // the Settings tab's use_sandbox_mode toggle.
    useSandboxMode: false,
    vatRate1: '',
    vatRate2: '',
  }
}

const initializeOpeningHoursData = (): OpeningHoursData => {
  return {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
    closingDays: [],
  }
}

const initializeMatrixInterfaceData = (): MatrixInterfaceData => {
  return {
    articleSetupMethod: null,
    isPremium: false,
    isLocked: false,
    allowMatrixPos: true,
    neoService: {
      endpoint: '',
      articleTitleFrom: 'name',
      dontUpdateTitle: false,
      dontUpdateDescription: false,
      preserveCategories: false,
      autoSync: false,
      assignMenuCards: false,
      menuCardAssignments: [],
      menus: [
        { orderMode: 'Self Service', selectedMenu: '', imported: false },
        { orderMode: 'Self Ordering', selectedMenu: '', imported: false },
        { orderMode: 'Kiosk', selectedMenu: '', imported: false },
      ],
    },
    universalPos: {
      endpoint: '',
      settlementTypeId: '',
      allowCustomerInvoice: false,
      httpServerPrefix: '',
      mode1: { terminalSerialNo: '', waiterId: '', waiterPassword: '', bookToTableNumber: '' },
      mode2: { terminalSerialNo: '', waiterId: '', waiterPassword: '' },
    },
  }
}

// Settings initialization moved to modules/settings.ts
// Order modes initialization moved to modules/orderModes.ts

export const useRestaurantInfoStore = defineStore('restaurantInfo', () => {
  const restaurantId = ref<string>('')
  const basicInformation = ref<BasicInformationData>(initializeBasicInformationData())
  const paymentGateway = ref<PaymentGatewayData>(initializePaymentGatewayData())
  const openingHours = ref<OpeningHoursData>(initializeOpeningHoursData())
  const matrixInterface = ref<MatrixInterfaceData>(initializeMatrixInterfaceData())

  // Step completion tracking - only step 1 is required to unlock step 2
  // Initialize from localStorage if available
  const loadCompletedStepsFromStorage = (): Set<number> => {
    try {
      const stored = localStorage.getItem('restaurantOnboardingCompletedSteps')
      if (stored) {
        const stepsArray = JSON.parse(stored)
        return new Set(stepsArray)
      }
    } catch (e) {
      console.warn('Failed to load completed steps from localStorage:', e)
    }
    return new Set()
  }

  const completedSteps = ref<Set<number>>(loadCompletedStepsFromStorage())

  // Order modes module composition
  const orderModesModule = useOrderModes()
  const orderModes = orderModesModule.orderModes
  const priceLevels = orderModesModule.priceLevels

  // Reception accounts module composition
  const receptionAccountsModule = useReceptionAccounts(() => restaurantId.value)
  const receptionAccounts = receptionAccountsModule.receptionAccounts

  // Pickup monitors module composition
  const pickupMonitorsModule = usePickupMonitors(() => restaurantId.value)
  const pickupMonitors = pickupMonitorsModule.monitors

  // SOT accounts module composition
  const sotAccountsModule = useSOTAccounts()
  const settingsModule = useSettings(() => restaurantId.value)

  // Single source for the price currency symbol: empty while the restaurant setting
  // "Do not show currency" is on, so every price display drops the symbol at once.
  const currencySymbol = computed(() =>
    basicInformation.value.hideCurrency ? '' : basicInformation.value.currency || ''
  )

  // General settings owns the flag, so keep basic information (loaded on every tab) in sync
  // after a load/save instead of waiting for the next restaurant-info fetch.
  const syncHideCurrency = () => {
    basicInformation.value.hideCurrency = settingsModule.settings.value.hideCurrency ?? false
  }
  const sotAccounts = sotAccountsModule.accounts

  // Service areas module composition
  const serviceAreasModule = useServiceAreas(() => restaurantId.value)
  const serviceAreas = serviceAreasModule.serviceAreas
  const serviceAreaSettings = serviceAreasModule.settings

  // Articles module composition
  const articlesModule = useArticles()
  const articles = articlesModule.articles
  const articlesTaxGateEnabled = articlesModule.taxGateEnabled
  const articlesMissingTaxCount = articlesModule.missingTaxCount
  const articlesDraft = articlesModule.draft

  // Bulk Edit module composition
  const bulkEditModule = useBulkEdit(() => articles.value)
  const selectedArticleIds = bulkEditModule.selectedIds

  // Offering Time Presets module composition
  const offeringTimePresetsModule = useOfferingTimePresets()
  const offeringTimePresets = offeringTimePresetsModule.presets

  // Price Levels module composition
  const priceLevelsModule = usePriceLevels(() => restaurantId.value)

  // Menu Languages module composition
  const menuLanguagesModule = useMenuLanguages(() => restaurantId.value)
  const menuLanguages = menuLanguagesModule.menuLanguages

  // Cloud Printers module composition
  const cloudPrintersModule = useCloudPrinters()
  const cloudPrinters = cloudPrintersModule.printers
  const cloudPrintersLoading = cloudPrintersModule.isLoading
  const cloudPrintersError = cloudPrintersModule.error

  // Menu Categories module composition
  const menuCategoriesModule = useMenuCategories()
  const menuCategories = menuCategoriesModule.categories

  // Food Categories module composition
  const foodCategoriesModule = useFoodCategories(() => restaurantId.value)
  const foodCategories = foodCategoriesModule.categories

  // Food Attributes module composition
  const foodAttributesModule = useFoodAttributes(() => restaurantId.value)
  const foodAttributesProperties = foodAttributesModule.properties
  const foodAttributesAllergens = foodAttributesModule.allergens
  const foodAttributesAdditives = foodAttributesModule.additives

  // Food Item Options module composition
  const foodItemOptionsModule = useFoodItemOptions()
  const foodItemOptions = foodItemOptionsModule.optionGroups

  // Orders module composition
  const ordersModule = useOrders()
  const orders = ordersModule.orders
  const ordersErrorSummary = ordersModule.errorSummary
  const currentPage = ordersModule.currentPage
  const lastPage = ordersModule.lastPage
  const total = ordersModule.total
  const perPage = ordersModule.perPage
  const isOrdersLoading = ordersModule.isLoading
  const ordersError = ordersModule.error
  const ordersSortBy = ordersModule.sortBy
  const ordersSortDir = ordersModule.sortDir
  const ordersFilters = ordersModule.filters

  // ===== INITIALIZATION METHODS =====
  const initializeNew = () => {
    restaurantId.value = ''
    basicInformation.value = initializeBasicInformationData()
    paymentGateway.value = initializePaymentGatewayData()
    openingHours.value = initializeOpeningHoursData()
    matrixInterface.value = initializeMatrixInterfaceData()
    completedSteps.value = new Set()
    priceLevelsModule.reset()
    menuLanguagesModule.reset()
    menuCategoriesModule.reset()
    cloudPrintersModule.reset()
    foodCategoriesModule.reset()
    foodAttributesModule.reset()
    foodItemOptionsModule.reset()
    articles.value = null
    articlesTaxGateEnabled.value = false
    articlesMissingTaxCount.value = 0
    bulkEditModule.deselectAll()
    settingsModule.initialize()
    serviceAreasModule.initialize()
    orderModesModule.reset()
    ordersModule.initialize()
    // Clear localStorage
    try {
      localStorage.removeItem('restaurantOnboardingCompletedSteps')
    } catch (e) {
      console.error('Failed to clear completed steps from localStorage:', e)
    }
  }

  const initializeForEdit = (id: string) => {
    restaurantId.value = id
  }

  const reset = () => {
    initializeNew()
    completedSteps.value = new Set()
    // Clear localStorage
    try {
      localStorage.removeItem('restaurantOnboardingCompletedSteps')
    } catch (e) {
      console.error('Failed to clear completed steps from localStorage:', e)
    }
  }

  // ===== STEP COMPLETION TRACKING =====
  const markStepAsCompleted = (stepNumber: number) => {
    completedSteps.value.add(stepNumber)
    // Persist to localStorage
    try {
      const stepsArray = Array.from(completedSteps.value)
      localStorage.setItem('restaurantOnboardingCompletedSteps', JSON.stringify(stepsArray))
    } catch (e) {
      console.error('Failed to persist completed steps to localStorage:', e)
    }
  }

  const isStepAccessible = (stepNumber: number): boolean => {
    // Step 1 is always accessible
    if (stepNumber === 1) return true

    // For subsequent steps, the previous step must be completed
    return completedSteps.value.has(stepNumber - 1)
  }

  const getAccessibleSteps = (): number[] => {
    const accessible: number[] = [1] // Step 1 is always accessible

    // Add subsequent steps if their previous step is completed
    for (let step = 2; step <= 5; step++) {
      if (isStepAccessible(step)) {
        accessible.push(step)
      }
    }

    return accessible
  }

  // ===== BASIC INFORMATION METHODS =====
  const updateBasicInformation = (updates: Partial<BasicInformationData>) => {
    basicInformation.value = { ...basicInformation.value, ...updates }
  }

  const saveBasicInformation = async (): Promise<{ success: boolean; restaurantId?: string; message: string; fieldErrors?: Record<string, string[]> }> => {
    try {
      const requestPayload: any = {
        restaurant_name: basicInformation.value.restaurantName,
        company_id: parseInt(basicInformation.value.companyId),
        street: basicInformation.value.street,
        zip_code: basicInformation.value.zipcode,
        city: basicInformation.value.city,
        default_lang: basicInformation.value.defaultLanguage,
        webpage_link: basicInformation.value.urlSuffix,
        manager_name: basicInformation.value.managerName,
        manager_email: basicInformation.value.managerEmail,
        manager_mobile_no: basicInformation.value.managerMobileNo,
      }

      // Add optional fields
      if (basicInformation.value.partnerId) {
        requestPayload.partner_id = parseInt(basicInformation.value.partnerId)
      }

      // Handle banner image and determine method (POST for create, PUT for edit)
      let response
      if (restaurantId.value) {
        // Edit mode: use PUT with restaurantId in URL
        if (basicInformation.value.bannerChanged) {
          requestPayload.restaurant_banner = basicInformation.value.bannerImage || ''
        }
        response = await apiInstance.put(`/restaurant/restaurant-info/${restaurantId.value}`, requestPayload)
      } else {
        // Create mode: use POST
        // Do not send bannerImage if it is the default image from public folder
        if (basicInformation.value.bannerImage && basicInformation.value.bannerImage !== '/flixorder-banner.jpg') {
          requestPayload.restaurant_banner = basicInformation.value.bannerImage
        }
        response = await apiInstance.post('/restaurant/restaurant-info', requestPayload)
      }

      const data = response.data

      if (data.error === false && data.data) {
        // Store the restaurant ID for subsequent operations
        if (data.data.restaurant_id) {
          restaurantId.value = data.data.restaurant_id.toString()
        }
        // Mark step 1 as completed
        markStepAsCompleted(1)
        return {
          success: true,
          restaurantId: data.data.restaurant_id?.toString(),
          message: 'Basic information saved successfully',
        }
      } else {
        const errorMessage = data.message || 'Failed to save restaurant information'
        console.error('Save restaurant-info error:', errorMessage, 'Response:', data)
        const error: any = new Error(errorMessage)
        if (data.errors && typeof data.errors === 'object') {
          error.fieldErrors = data.errors
        }
        throw error
      }
    } catch (e: any) {
      console.error('❌ Error saving restaurant info:', e)
      console.error('📊 Error type:', e.constructor.name)
      console.error('📊 Error status:', e.response?.status)
      console.error('📊 Error data:', JSON.stringify(e.response?.data))

      // Ensure we have response data
      if (!e.response?.data) {
        console.error('⚠️ No response data available')
        throw new Error(e.message || 'Network error while saving restaurant information')
      }

      const responseData = e.response.data
      console.error('📊 Response error flag:', responseData.error)
      console.error('📊 Response errors:', JSON.stringify(responseData.errors))

      // Handle validation errors (422) - check for errors object in response
      if ((e.response?.status === 422 || responseData.error === true) && responseData.errors) {
        const error: any = new Error('Validation failed. Please check the form for errors.')
        error.fieldErrors = responseData.errors
        throw error
      }

      // Handle other errors
      const error: any = new Error(
        responseData.message || e.message || 'Network error while saving restaurant information'
      )
      if (responseData.errors && typeof responseData.errors === 'object') {
        error.fieldErrors = responseData.errors
      }
      throw error
    }
  }

  const loadBasicInformation = async (id: string): Promise<void> => {
    // Unknown until this restaurant's info arrives, so a previous restaurant's status is never shown
    basicInformation.value.isActive = undefined
    try {
      const response = await apiInstance.get(`/restaurant/restaurant-info/${id}`)
      const apiData = response.data.data

      if (response.data.error === false && apiData) {
        // Load data into store
        basicInformation.value = {
          restaurantName: apiData.restaurant_name || '',
          street: apiData.street || '',
          city: apiData.city || '',
          zipcode: apiData.zip_code || '',
          hasOrder: apiData.has_order === 1,
          isActive: apiData.status === 1,
          bannerImage: apiData.restaurant_banner || '',
          partnerId: apiData.partner_id?.toString() || '',
          companyId: apiData.company_id?.toString() || '',
          managerName: apiData.manager_name || '',
          managerEmail: apiData.manager_email || '',
          managerMobileNo: apiData.manager_mobile_no || '',
          urlSuffix: apiData.webpage_link || '',
          currency: apiData.currency_code || '',
          hideCurrency: apiData.hide_currency === 1 || apiData.hide_currency === true,
          defaultLanguage: (apiData.default_lang as 'en' | 'de') || 'de',
        }

        // Kept in sync here too: this endpoint is loaded on every tab, general settings is not.
        settingsModule.setModuleVisibility(
          apiData.show_matrix_interface === 1 || apiData.show_matrix_interface === true,
          apiData.show_cloud_printer === 1 || apiData.show_cloud_printer === true,
          apiData.show_kiosk === 1 || apiData.show_kiosk === true
        )

      } else {
        let errorMessage = response.data.message || 'Failed to fetch restaurant information'
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      console.error('Error loading restaurant info:', e)
      throw e
    }
  }

  // ===== PAYMENT GATEWAY METHODS =====
  const updatePaymentGateway = (updates: Partial<PaymentGatewayData>) => {
    paymentGateway.value = { ...paymentGateway.value, ...updates }
  }

  const savePaymentGateway = async (): Promise<{ success: boolean; message: string; fieldErrors?: Record<string, string[]> }> => {
    try {
      if (!restaurantId.value) {
        throw new Error('Restaurant ID is required to save payment gateway settings')
      }

      const requestPayload: any = {}

      // Check if any payment field has a value
      const hasAnyAdyenValue = !!(paymentGateway.value.adyenConfig.apiKey || paymentGateway.value.adyenConfig.merchantAccount || paymentGateway.value.adyenConfig.clientKey || paymentGateway.value.adyenConfig.hmacKey || paymentGateway.value.adyenConfig.apiUriPrefix)
      const hasAnyVrPayValue = !!(paymentGateway.value.vrPayConfig.merchantId || paymentGateway.value.vrPayConfig.blowfishPassword || paymentGateway.value.vrPayConfig.hmacPassword)

      // If no payment fields have any value, show the "no payment method" error
      if (!hasAnyAdyenValue && !hasAnyVrPayValue) {
        const error: any = new Error('Please add at least one payment method to continue')
        throw error
      }

      // One credential set per gateway is used for every restaurant now — use_sandbox_mode
      // (Settings tab) decides which endpoint they're sent to, not which field set is read.
      requestPayload.adyen_API_key = paymentGateway.value.adyenConfig.apiKey
      requestPayload.adyen_merchant_code = paymentGateway.value.adyenConfig.merchantAccount
      requestPayload.adyen_client_key = paymentGateway.value.adyenConfig.clientKey
      requestPayload.adyen_HMAC_key = paymentGateway.value.adyenConfig.hmacKey
      requestPayload.adyen_api_prefix = paymentGateway.value.adyenConfig.apiUriPrefix
      requestPayload.googlpay_merchantid_live = paymentGateway.value.adyenConfig.googlePayMerchantId
      if (paymentGateway.value.adyenConfig.companyAccount) {
        requestPayload.adyen_company_account = paymentGateway.value.adyenConfig.companyAccount
      }

      requestPayload.vr_pay_merchant_id = paymentGateway.value.vrPayConfig.merchantId
      requestPayload.vr_pay_blowfish_pass = paymentGateway.value.vrPayConfig.blowfishPassword
      requestPayload.vr_pay_hmac_pass = paymentGateway.value.vrPayConfig.hmacPassword

      // Active Payment Gateway
      if (paymentGateway.value.activePaymentGateway) {
        requestPayload.payment_gateway = paymentGateway.value.activePaymentGateway
      }

      // Remove keys with empty string values, but preserve payment gateway fields
      const paymentGatewayKeys = [
        'adyen_API_key', 'adyen_merchant_code', 'adyen_client_key', 'adyen_HMAC_key', 'adyen_api_prefix', 'adyen_company_account', 'googlpay_merchantid_live',
        'vr_pay_merchant_id', 'vr_pay_blowfish_pass', 'vr_pay_hmac_pass',
      ]

      Object.keys(requestPayload).forEach(key => {
        if (Array.isArray(requestPayload[key])) {
          // Keep arrays as-is
        } else if (typeof requestPayload[key] === 'object') {
          // Keep objects as-is
        } else if (paymentGatewayKeys.includes(key)) {
          // Always preserve payment gateway fields, even if empty
        } else if (requestPayload[key] === undefined || requestPayload[key] === '') {
          // Remove other empty fields
          delete requestPayload[key]
        }
      })

      const response = await apiInstance.put(`/restaurant/payment-bank-vat/${restaurantId.value}`, requestPayload)

      // Check for success (error: false or 0) within a 2xx response
      if ((response.data.error === false || response.data.error === 0) && response.status >= 200 && response.status < 300) {
        return {
          success: true,
          message: 'Payment gateway settings saved successfully',
        }
      } else {
        const errorMessage = response.data.message || 'Failed to save payment gateway settings'
        const error: any = new Error(errorMessage)
        if (response.data.errors && typeof response.data.errors === 'object') {
          error.fieldErrors = response.data.errors
        }
        throw error
      }
    } catch (e: any) {
      console.error('Error saving payment gateway:', e)

      if (e.response?.status === 422 && e.response?.data?.errors) {
        const error: any = new Error('Validation failed. Please check the form for errors.')
        error.fieldErrors = e.response.data.errors
        throw error
      }

      const error: any = new Error(
        e.response?.data?.message || e.message || 'Network error while saving payment gateway settings'
      )
      if (e.response?.data?.errors && typeof e.response.data.errors === 'object') {
        error.fieldErrors = e.response.data.errors
      }
      throw error
    }
  }

  const loadPaymentGateway = async (id: string): Promise<void> => {
    try {
      restaurantId.value = id
      const response = await apiInstance.get(`/restaurant/payment-bank-vat/${id}`)

      if (response.data.error === false && response.data.data) {
        const apiData = response.data.data

        paymentGateway.value = {
          adyenConfig: {
            merchantAccount: apiData.adyen_merchant_code,
            apiKey: apiData.adyen_API_key,
            companyAccount: apiData.adyen_company_account,
            clientKey: apiData.adyen_client_key,
            hmacKey: apiData.adyen_HMAC_key,
            apiUriPrefix: apiData.adyen_api_prefix,
            googlePayMerchantId: apiData.googlpay_merchantid_live,
          },
          vrPayConfig: {
            merchantId: apiData.vr_pay_merchant_id,
            blowfishPassword: apiData.vr_pay_blowfish_pass,
            hmacPassword: apiData.vr_pay_hmac_pass,
          },
          activePaymentGateway: apiData.payment_gateway || 'adyen',
          useSandboxMode: !!apiData.use_sandbox_mode,
          vatRate1: apiData.vat_rates?.[0]?.vat_rate_1?.toString() || '',
          vatRate2: apiData.vat_rates?.[1]?.vat_rate_2?.toString() || '',
        }

      } else {
        throw new Error(response.data.message || 'Failed to fetch payment gateway settings')
      }
    } catch (e: any) {
      console.error('Error loading payment gateway:', e)
      throw new Error(e.response?.data?.message || e.message || 'Network error while fetching payment gateway settings')
    }
  }

  // ===== OPENING HOURS METHODS =====
  const updateOpeningHours = (updates: Partial<OpeningHoursData>) => {
    openingHours.value = { ...openingHours.value, ...updates }
  }

  const saveOpeningHours = async (): Promise<{ success: boolean; message: string; fieldErrors?: Record<string, string[]> }> => {
    try {
      // Transform opening hours to API format: { opening_hours: [{ week_day, available_timings: ["HH:MM-HH:MM"] }], closing_days }
      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      const opening_hours = weekdays.map((day) => {
        const slots = openingHours.value[day as keyof OpeningHoursData] as TimeSlot[] | undefined
        return {
          week_day: day,
          available_timings: Array.isArray(slots) && slots.length > 0
            ? slots.map((slot) => `${slot.startTime}-${slot.endTime}`)
            : [],
        }
      })

      // Convert closing days from "YYYY-MM-DD" to "DD-MM-YYYY" format for API
      const closing_days = Array.isArray(openingHours.value.closingDays)
        ? openingHours.value.closingDays.map((dateStr: string) => {
            const [year, month, day] = dateStr.split('-')
            return `${day}-${month}-${year}`
          })
        : []

      const requestPayload = {
        opening_hours,
        closing_days,
      }

      // Use PUT with restaurant ID in URL path
      const response = await apiInstance.put(`/restaurant/opening-hours/${restaurantId.value}`, requestPayload)
      const data = response.data

      if (data.error === false || response.status === 200) {
        return {
          success: true,
          message: data.message || 'Opening hours saved successfully',
        }
      } else {
        const errorMessage = data.message || 'Failed to save opening hours'
        console.error('❌ Error saving opening hours:', errorMessage, 'Response:', data)
        const error: any = new Error(errorMessage)
        if (data.errors && typeof data.errors === 'object') {
          error.fieldErrors = data.errors
        }
        throw error
      }
    } catch (e: any) {
      console.error('Error saving opening hours:', {
        message: e.message,
        status: e.response?.status,
        data: e.response?.data,
      })

      // Handle validation errors (422)
      if (e.response?.status === 422) {
        const fieldErrors = e.response.data?.errors || e.response.data?.data?.errors || {}
        const error: any = new Error('Validation failed. Please check the form for errors.')
        error.fieldErrors = fieldErrors
        console.error('422 Validation error - fieldErrors:', {
          fieldErrors: error.fieldErrors,
          responseDataErrors: e.response.data?.errors,
          fullResponseData: e.response.data,
        })
        throw error
      }

      // Handle other errors
      const error: any = new Error(
        e.response?.data?.message || e.message || 'Network error while saving opening hours'
      )
      if (e.response?.data?.errors && typeof e.response.data.errors === 'object') {
        error.fieldErrors = e.response.data.errors
      }
      throw error
    }
  }

  const loadOpeningHours = async (id: string): Promise<void> => {
    try {
      if (!id) {
        console.warn('⚠️ Restaurant ID is required. Cannot load opening hours.')
        return
      }

      // Use GET with restaurant ID in URL path
      const response = await apiInstance.get(`/restaurant/opening-hours/${id}`)
      const data = response.data

      console.log('📥 loadOpeningHours API response:', { data, status: response.status })

      if ((data.error === false || response.status === 200) && data.data) {
        // Transform API format to internal format
        const apiData = data.data
        const newOpeningHours: OpeningHoursData = initializeOpeningHoursData()
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

        // Check if API response is in new object format or old array format
        const isNewFormat = !Array.isArray(apiData.opening_hours) && !Array.isArray(apiData)

        if (isNewFormat) {
          // New format: { "Monday": [{from: "09:00", to: "22:00"}], "Tuesday": [], ... }

          days.forEach((day) => {
            if (apiData.hasOwnProperty(day)) {
              const dayTimings = apiData[day]

              if (Array.isArray(dayTimings) && dayTimings.length > 0) {
                newOpeningHours[day as keyof OpeningHoursData] = dayTimings.map((slot: any) => {
                  // Handle format: { from: "09:00", to: "22:00" }
                  if (slot.from && slot.to) {
                    return { startTime: slot.from, endTime: slot.to }
                  }
                  // Handle string format: "09:00-22:00"
                  else if (typeof slot === 'string') {
                    const [startTime, endTime] = slot.split('-')
                    return { startTime, endTime }
                  }
                  // Handle format: { startTime: "09:00", endTime: "22:00" }
                  else if (slot.startTime && slot.endTime) {
                    return { startTime: slot.startTime, endTime: slot.endTime }
                  } else {
                    console.warn(`⚠️ Unexpected slot format for ${day}:`, slot)
                    return { startTime: '09:00', endTime: '17:00' }
                  }
                }) as TimeSlot[]
              } else {
                // Empty array - day is closed
                newOpeningHours[day as keyof OpeningHoursData] = []
              }
            } else {
              console.warn(`⚠️ Day "${day}" not found in API response`)
            }
          })
        } else if (Array.isArray(apiData.opening_hours)) {
          // Old array format: [{ week_day: "Monday", available_timings: [...] }, ...]
          apiData.opening_hours.forEach((dayData: any) => {
            const day = dayData.week_day
            if (newOpeningHours.hasOwnProperty(day)) {
              newOpeningHours[day as keyof OpeningHoursData] = (
                Array.isArray(dayData.available_timings)
                  ? dayData.available_timings.map((timing: string) => {
                      const [startTime, endTime] = timing.split('-')
                      return { startTime, endTime }
                    })
                  : []
              ) as TimeSlot[]
            } else {
              console.warn(`⚠️ Day "${day}" not recognized in openingHours object`)
            }
          })
        } else {
        }

        // Process closing_days - convert from "DD-MM-YYYY" to "YYYY-MM-DD"
        if (Array.isArray(apiData.closing_days) && apiData.closing_days.length > 0) {
          newOpeningHours.closingDays = apiData.closing_days.map((dateStr: string) => {
            const [day, month, year] = dateStr.split('-')
            return `${year}-${month}-${day}`
          })
        } else {
        }

        openingHours.value = newOpeningHours
        console.log('✅ Loaded opening hours into store:', newOpeningHours)
      } else {
        const errorMessage = data.message || 'Failed to load opening hours - no data in response'
        console.error('❌ Error loading opening hours:', errorMessage, { data })
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      console.error('❌ Error loading opening hours:', e)
      console.error('📊 Error response:', e.response?.data)
      throw e
    }
  }

  // ===== MATRIX INTERFACE METHODS =====
  const updateMatrixInterface = (updates: Partial<MatrixInterfaceData>) => {
    matrixInterface.value = { ...matrixInterface.value, ...updates }
  }

  const testNeoService = async (endpoint: string): Promise<{ success: boolean; message: string }> => {
    try {
      if (!restaurantId.value) {
        throw new Error('Restaurant ID is required to test Neo Service connection')
      }

      const requestPayload = {
        restaurant_id: parseInt(restaurantId.value),
        endpoint_value: endpoint,
      }

      const response = await apiInstance.post(`/restaurant/interface/neoservice-setting/test`, requestPayload)
      const data = response.data

      if (data.error === false || response.status === 200) {
        return {
          success: true,
          message: data.message || 'Neo Service connection successful',
        }
      } else {
        const errorMessage = data.message || 'Neo Service connection failed'
        console.error('❌ Neo Service test failed:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      console.error('❌ Error testing Neo Service:', e)
      const error = new Error(
        e.response?.data?.message || e.message || 'Failed to test Neo Service connection. Please check the endpoint and try again.'
      )
      throw error
    }
  }

  const testUniversalPos = async (endpoint: string): Promise<{ success: boolean; message: string }> => {
    try {
      if (!restaurantId.value) {
        throw new Error('Restaurant ID is required to test Universal POS connection')
      }

      const requestPayload = {
        restaurant_id: parseInt(restaurantId.value),
        endpoint_universal_pos: endpoint,
      }

      const response = await apiInstance.post(`/restaurant/interface/universalpos-setting/test`, requestPayload)
      const data = response.data

      if (data.error === false || response.status === 200) {
        return {
          success: true,
          message: data.message || 'Universal POS connection successful',
        }
      } else {
        const errorMessage = data.message || 'Universal POS connection failed'
        console.error('❌ Universal POS test failed:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      console.error('❌ Error testing Universal POS:', e)
      const error = new Error(
        e.response?.data?.message || e.message || 'Failed to test Universal POS connection. Please check the endpoint and try again.'
      )
      throw error
    }
  }

  const saveNeoServiceConfig = async (): Promise<{ success: boolean; message: string }> => {
    try {
      if (!restaurantId.value) {
        throw new Error('Restaurant ID is required to save Neo Service configuration')
      }

      if (!matrixInterface.value.articleSetupMethod) {
        throw new Error('A setup method must be selected before saving')
      }

      // Map articleSetupMethod to interface_option
      // 'import' = 'matrix_pos' (Connect Matrix Interface & Import Articles)
      // 'manual' = 'none' (Create Articles Manually)
      const interfaceOptionMap: Record<'import' | 'manual', string> = {
        'import': 'matrix_pos',
        'manual': 'none',
      }
      const interfaceOption = interfaceOptionMap[matrixInterface.value.articleSetupMethod]

      // Endpoint is only required for import mode
      if (interfaceOption === 'matrix_pos' && !matrixInterface.value.neoService.endpoint) {
        throw new Error('Neo Service endpoint is required for import mode')
      }

      // Map articleTitleFrom to data_mapping_type
      const dataMappingMap: Record<string, string> = {
        'name': 'article_name',
        'displayText': 'display_text',
        'printerText': 'printer_text',
      }
      const dataMappingType = dataMappingMap[matrixInterface.value.neoService.articleTitleFrom] || 'article_name'

      const requestPayload: any = {
        interface_option: interfaceOption,
        data_mapping_type: dataMappingType,
        preserve_mainsub_cate: matrixInterface.value.neoService.preserveCategories,
        auto_sync: matrixInterface.value.neoService.autoSync,
        dont_import_desc: matrixInterface.value.neoService.dontUpdateDescription,
        dont_import_title: matrixInterface.value.neoService.dontUpdateTitle,
        assign_menu_cards: matrixInterface.value.neoService.assignMenuCards,
      }

      // Only include endpoint for import mode
      if (interfaceOption === 'matrix_pos' || matrixInterface.value.neoService.endpoint) {
        requestPayload.endpoint_value = matrixInterface.value.neoService.endpoint
      }

      // Only include mode assignments when the toggle is enabled
      if (matrixInterface.value.neoService.assignMenuCards) {
        requestPayload.menu_card_assignments = matrixInterface.value.neoService.menuCardAssignments || []
      }

      const response = await apiInstance.put(`/restaurant/interface/neoservice-setting/${restaurantId.value}`, requestPayload)
      const data = response.data

      if (data.error === false || response.status === 200) {
        /* The setup method is final once saved — lock it immediately instead of
        waiting for a reload, so it can't be switched again from this same
        session. */
        matrixInterface.value.isLocked = true

        return {
          success: true,
          message: data.message || 'Neo Service configuration saved successfully',
        }
      } else {
        const errorMessage = data.message || 'Failed to save Neo Service configuration'
        console.error('❌ Error saving Neo Service configuration:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      console.error('❌ Error saving Neo Service configuration:', e)
      const error = new Error(
        e.response?.data?.message || e.message || 'Failed to save Neo Service configuration. Please try again.'
      )
      throw error
    }
  }

  /* Un-finalizes a saved setup-method choice while still mid-onboarding (e.g.
  going back via Previous), so it can be picked again instead of staying
  locked from a save that hasn't actually been confirmed as final yet. */
  const resetNeoServiceConfig = async (): Promise<void> => {
    try {
      if (!restaurantId.value) return

      await apiInstance.post(`/restaurant/interface/neoservice-setting/${restaurantId.value}/reset`)
      matrixInterface.value.isLocked = false
    } catch (e: any) {
      console.error('❌ Error resetting Neo Service configuration:', e)
    }
  }

  const saveUniversalPosConfig = async (): Promise<{ success: boolean; message: string; fieldErrors?: Record<string, string[]> }> => {
    try {
      if (!restaurantId.value) {
        throw new Error('Restaurant ID is required to save Universal POS configuration')
      }

      if (!matrixInterface.value.universalPos.endpoint) {
        throw new Error('Universal POS endpoint is required')
      }

      // Transform form data to API format
      const requestPayload = {
        endpoint_universal_pos: matrixInterface.value.universalPos.endpoint,
        settlement_ID: parseInt(matrixInterface.value.universalPos.settlementTypeId) || 0,
        http_server_active: matrixInterface.value.universalPos.allowCustomerInvoice ? 1 : 0,
        http_server: matrixInterface.value.universalPos.httpServerPrefix || '',
        modes: {
          1: {
            device_ID: matrixInterface.value.universalPos.mode1.terminalSerialNo,
            waiter_ID: matrixInterface.value.universalPos.mode1.waiterId,
            endpoint_password: matrixInterface.value.universalPos.mode1.waiterPassword || '',
            order_mode_tableno: parseInt(matrixInterface.value.universalPos.mode1.bookToTableNumber) || 0,
          },
          2: {
            device_ID: matrixInterface.value.universalPos.mode2.terminalSerialNo,
            waiter_ID: matrixInterface.value.universalPos.mode2.waiterId,
            endpoint_password: matrixInterface.value.universalPos.mode2.waiterPassword || '',
          },
        },
      }

      const response = await apiInstance.put(`/restaurant/interface/universalpos-setting/${restaurantId.value}`, requestPayload)
      const data = response.data

      if (data.error === false || response.status === 200) {
        return {
          success: true,
          message: data.message || 'Universal POS configuration saved successfully',
        }
      } else {
        const errorMessage = data.message || 'Failed to save Universal POS configuration'
        console.error('❌ Error saving Universal POS configuration:', errorMessage)
        const error: any = new Error(errorMessage)
        if (data.errors && typeof data.errors === 'object') {
          error.fieldErrors = data.errors
        }
        throw error
      }
    } catch (e: any) {
      console.error('❌ Error saving Universal POS configuration:', e)
      const error: any = new Error()
      if (e.response?.data?.errors && typeof e.response.data.errors === 'object') {
        error.fieldErrors = e.response.data.errors
      }
      throw error
    }
  }

  const saveMatrixInterface = (): Promise<{ success: boolean; message: string }> => {
    return new Promise((resolve) => {
      resolve({
        success: true,
        message: 'Matrix Interface configuration saved successfully',
      })
    })
  }

  const loadNeoServiceConfig = async (id: string): Promise<void> => {
    try {
      if (!id) {
        console.warn('⚠️ Restaurant ID is required. Cannot load Neo Service configuration.')
        return
      }

      const response = await apiInstance.get(`/restaurant/interface/neoservice-setting/${id}`)
      const data = response.data

      if (data.error === false && data.data) {
        const apiData = data.data

        // Map interface_option back to articleSetupMethod
        const interfaceOptionMap: Record<string, 'import' | 'manual'> = {
          'matrix_pos': 'import',
          'none': 'manual',
        }

        // Map data_mapping_type back to articleTitleFrom
        const dataMappingMap: Record<string, string> = {
          'article_name': 'name',
          'display_text': 'displayText',
          'printer_text': 'printerText',
        }
        const articleTitleFrom = (dataMappingMap[apiData.data_mapping_type] || 'name') as 'name' | 'displayText' | 'printerText'

        // Update store — only set the setup method when the backend reports a real,
        // saved choice. interface_option is null until one is saved; defaulting that
        // to either option pre-selects a radio the admin never actually chose. Leave
        // it null so the Matrix Interface step renders with nothing selected.
        matrixInterface.value.articleSetupMethod = (apiData.interface_option && interfaceOptionMap[apiData.interface_option])
          ? interfaceOptionMap[apiData.interface_option]
          : null
        matrixInterface.value.isPremium = apiData.is_premium === true
        matrixInterface.value.isLocked = apiData.interface_option_locked === true
        matrixInterface.value.allowMatrixPos = apiData.matrix_pos_allowed !== false
        matrixInterface.value.neoService = {
          endpoint: apiData.endpoint_value || '',
          articleTitleFrom: articleTitleFrom,
          dontUpdateTitle: apiData.dont_import_title === 1,
          dontUpdateDescription: apiData.dont_import_desc === 1,
          preserveCategories: apiData.preserve_mainsub_cate === 1,
          autoSync: apiData.auto_sync === 1,
          assignMenuCards: apiData.assign_menu_cards === 1 || apiData.assign_menu_cards === true,
          menuCardAssignments: Array.isArray(apiData.menu_card_assignments) ? apiData.menu_card_assignments : [],
          menus: matrixInterface.value.neoService.menus || [],
        }

      } else {
        console.warn('⚠️ Could not load Neo Service configuration (may not exist yet)')
      }
    } catch (e: any) {
      console.warn('⚠️ Could not load Neo Service configuration:', e.message)
    }
  }

  const loadUniversalPosConfig = async (id: string): Promise<void> => {
    try {
      if (!id) {
        console.warn('⚠️ Restaurant ID is required. Cannot load Universal POS configuration.')
        return
      }

      const response = await apiInstance.get(`/restaurant/interface/universalpos-setting/${id}`)
      const data = response.data

      if (data.error === false && data.data) {
        const apiData = data.data

        // Initialize modes with default values
        const modes = {
          1: {
            terminalSerialNo: apiData.modes?.['1']?.device_ID || '',
            waiterId: apiData.modes?.['1']?.waiter_ID || '',
            waiterPassword: apiData.modes?.['1']?.endpoint_password || '',
            bookToTableNumber: apiData.modes?.['1']?.order_mode_tableno?.toString() || '',
            tableNumber: apiData.modes?.['1']?.order_mode_tableno?.toString() || '',
          },
          2: {
            terminalSerialNo: apiData.modes?.['2']?.device_ID || '',
            waiterId: apiData.modes?.['2']?.waiter_ID || '',
            waiterPassword: apiData.modes?.['2']?.endpoint_password || '',
            bookToTableNumber: apiData.modes?.['2']?.order_mode_tableno?.toString() || '',
            tableNumber: apiData.modes?.['2']?.order_mode_tableno?.toString() || '',
          },
          3: {
            terminalSerialNo: apiData.modes?.['3']?.device_ID || '',
            waiterId: apiData.modes?.['3']?.waiter_ID || '',
            waiterPassword: apiData.modes?.['3']?.endpoint_password || '',
            bookToTableNumber: apiData.modes?.['3']?.order_mode_tableno?.toString() || '',
            tableNumber: apiData.modes?.['3']?.order_mode_tableno?.toString() || '',
          },
        }

        // Update store
        matrixInterface.value.universalPos = {
          endpoint: apiData.endpoint_universal_pos || '',
          settlementTypeId: apiData.settlement_ID?.toString() || '',
          allowCustomerInvoice: apiData.http_server_active === 1,
          httpServerPrefix: apiData.http_server || '',
          mode1: modes[1],
          mode2: modes[2],
        }

      } else {
        console.warn('⚠️ Could not load Universal POS configuration (may not exist yet)')
      }
    } catch (e: any) {
      console.warn('⚠️ Could not load Universal POS configuration:', e.message)
    }
  }

  const loadMenucardData = async (id: string): Promise<{ modes: OrderMode[]; preSelected: Record<number, string> }> => {
    try {
      if (!id) {
        console.warn('⚠️ Restaurant ID is required. Cannot load menucard data.')
        return { modes: [], preSelected: {} }
      }

      const response = await apiInstance.get(`/restaurant/interface/menucard-data-list/${id}`)
      const data = response.data

      if (data.error === false && data.data && data.data.modes) {

        // Update the store with modes data
        matrixInterface.value.neoService.modes = data.data.modes || []

        // Extract pre-selected values from the API response
        const preSelected: Record<number, string> = {}
        for (const mode of data.data.modes) {
          const selectedOption = mode.options?.find((opt: MenuOption) => opt.selected)
          if (selectedOption && selectedOption.value !== null && selectedOption.value !== undefined && selectedOption.value !== '') {
            preSelected[mode.mode_id] = String(selectedOption.value)
          }
        }

        return { modes: data.data.modes, preSelected }
      } else {
        console.warn('⚠️ Could not load menucard data')
        return { modes: [], preSelected: {} }
      }
    } catch (e: any) {
      console.warn('��️ Error loading menucard data:', e.message)
      return { modes: [], preSelected: {} }
    }
  }

  const loadMatrixInterface = async (id: string): Promise<void> => {
    try {
      if (!id) {
        console.warn('⚠️ Restaurant ID is required. Cannot load matrix interface.')
        return
      }

      // Load both Neo Service and Universal POS configurations in parallel
      await Promise.all([
        loadNeoServiceConfig(id),
        loadUniversalPosConfig(id),
      ])

    } catch (e: any) {
      console.warn('⚠️ Could not load matrix interface settings:', e.message)
    }
  }

  // ===== RESTAURANT SETTINGS METHODS =====
  // Settings methods are now in the settings module and re-exported below

  // Order modes methods are now in the orderModes module and re-exported below

  // ===== DEPRECATED METHODS (for backward compatibility) =====
  const updateData = (updates: Partial<BasicInformationData>) => {
    updateBasicInformation(updates)
  }

  const loadRestaurantInfo = (id: string): Promise<void> => {
    return loadBasicInformation(id)
  }

  return {
    // State
    restaurantId,
    basicInformation,
    currencySymbol,
    openingHours,
    paymentGateway,
    matrixInterface,
    articles,
    articlesTaxGateEnabled,
    articlesMissingTaxCount,
    articlesDraft,
    selectedArticleIds,
    menuLanguages,
    restaurantPriceLevels: priceLevelsModule.levels,
    offeringTimePresets,
    settings: settingsModule.settings,
    orderModes,
    receptionAccounts,
    pickupMonitors,
    sotAccounts,
    kiosks: sotAccountsModule.kiosks,
    kioskLoading: sotAccountsModule.kioskLoading,
    kioskError: sotAccountsModule.kioskError,
    serviceAreas,
    serviceAreaSettings,
    priceLevels,
    completedSteps,

    // Initialization
    initializeNew,
    initializeForEdit,
    reset,

    // Step Completion Tracking
    markStepAsCompleted,
    isStepAccessible,
    getAccessibleSteps,

    // Basic Information
    updateBasicInformation,
    saveBasicInformation,
    loadBasicInformation,

    // Opening Hours
    updateOpeningHours,
    saveOpeningHours,
    loadOpeningHours,

    // Payment Gateway
    updatePaymentGateway,
    savePaymentGateway,
    loadPaymentGateway,

    // Matrix Interface
    updateMatrixInterface,
    testNeoService,
    testUniversalPos,
    saveNeoServiceConfig,
    resetNeoServiceConfig,
    saveUniversalPosConfig,
    saveMatrixInterface,
    loadNeoServiceConfig,
    loadUniversalPosConfig,
    loadMatrixInterface,
    loadMenucardData,

    // Settings (from module)
    updateSettings: settingsModule.updateDraft,
    initializeSettingsDraft: settingsModule.initializeDraft,
    getSettingsDraft: settingsModule.getDraft,
    loadGeneralSettings: async () => {
      const result = await settingsModule.loadSettings()
      syncHideCurrency()
      return result
    },
    saveGeneralSettings: async (settingsData?: RestaurantSettingsData) => {
      const dataToSave = settingsData || settingsModule.settings.value
      const result = await settingsModule.saveSettings(dataToSave)
      syncHideCurrency()
      return result
    },

    // Order Modes (from module)
    updateOrderModes: orderModesModule.updateOrderModes,
    updateMode1: orderModesModule.updateMode1,
    updateMode2: orderModesModule.updateMode2,
    loadReceptionAccounts: orderModesModule.loadReceptionAccounts,
    loadPriceLevels: orderModesModule.loadPriceLevels,
    loadOrderModesDropdownData: orderModesModule.loadOrderModesDropdownData,
    loadOrderModeSettings: orderModesModule.loadOrderModeSettings,
    saveOrderModeSettings: orderModesModule.saveOrderModeSettings,

    // Reception Accounts (from module)
    loadReceptionAccountsData: receptionAccountsModule.loadReceptionAccounts,
    suggestReceptionAccountName: receptionAccountsModule.suggestAccountName,
    createReceptionAccount: receptionAccountsModule.createAccount,
    updateReceptionAccount: receptionAccountsModule.updateAccount,
    deleteReceptionAccount: receptionAccountsModule.deleteAccount,
    toggleReceptionAccountStatus: receptionAccountsModule.toggleAccountStatus,
    getReceptionAccountById: receptionAccountsModule.getAccountById,
    getRestaurantReceptionAccounts: receptionAccountsModule.getRestaurantAccounts,
    getReceptionAccountDetails: receptionAccountsModule.getAccountDetails,
    uploadReceptionMedia: receptionAccountsModule.uploadMedia,
    deleteReceptionMedia: receptionAccountsModule.deleteMedia,
    generateReceptionLoginToken: receptionAccountsModule.generateLoginToken,

    // Pickup Monitors (from module)
    createPickupMonitor: pickupMonitorsModule.createMonitor,
    createPickupMonitorAPI: pickupMonitorsModule.createPickupMonitorAPI,
    updatePickupMonitor: pickupMonitorsModule.updateMonitor,
    deletePickupMonitor: pickupMonitorsModule.deleteMonitor,
    togglePickupMonitorStatus: pickupMonitorsModule.toggleMonitorStatus,
    getPickupMonitorById: pickupMonitorsModule.getMonitorById,
    getRestaurantPickupMonitors: pickupMonitorsModule.getRestaurantMonitors,
    getFamiliseModes: pickupMonitorsModule.getFamiliseModes,
    getBonModes: pickupMonitorsModule.getBonModes,
    getPickupModes: pickupMonitorsModule.getModes,
    loadPickupSubCategories: pickupMonitorsModule.loadSubCategories,
    loadPickupBonMonitors: pickupMonitorsModule.loadBonMonitors,
    initializeRestaurantPickupMonitors: pickupMonitorsModule.initializeRestaurantMonitors,
    loadPickupMonitors: pickupMonitorsModule.loadRestaurantPickupMonitors,
    loadPickupMonitorDetails: pickupMonitorsModule.loadPickupMonitorDetails,
    uploadPickupMedia: pickupMonitorsModule.uploadPickupMedia,
    deletePickupMedia: pickupMonitorsModule.deletePickupMedia,
    updatePickupMonitorAPI: pickupMonitorsModule.updatePickupMonitorAPI,
    generatePickupMonitorIntegrationToken: pickupMonitorsModule.generateIntegrationToken,
    suggestPickupMonitorAccountName: pickupMonitorsModule.suggestAccountName,
    initializePickupMonitorDraft: pickupMonitorsModule.initializeDraft,
    updatePickupMonitorDraft: pickupMonitorsModule.updateDraft,
    getPickupMonitorDraft: pickupMonitorsModule.getDraft,
    clearPickupMonitorDraft: pickupMonitorsModule.clearDraft,

    // SOT Accounts (from module)
    createSOTAccount: sotAccountsModule.createAccount,
    updateSOTAccount: sotAccountsModule.updateAccount,
    deleteSOTAccount: sotAccountsModule.deleteAccount,
    toggleSOTAccountStatus: sotAccountsModule.toggleAccountStatus,
    getSOTAccountById: sotAccountsModule.getAccountById,
    getRestaurantSOTAccounts: sotAccountsModule.getRestaurantAccounts,
    initializeRestaurantSOTAccounts: sotAccountsModule.initializeRestaurantAccounts,
    initializeSOTAccountDraft: sotAccountsModule.initializeDraft,
    updateSOTAccountDraft: sotAccountsModule.updateDraft,
    getSOTAccountDraft: sotAccountsModule.getDraft,
    clearSOTAccountDraft: sotAccountsModule.clearDraft,

    // Kiosks (from sotAccounts module)
    fetchKiosks: sotAccountsModule.fetchKiosks,
    deleteKiosk: sotAccountsModule.deleteKiosk,
    updateKioskVersion: sotAccountsModule.updateKioskVersion,
    fetchKioskPrinters: sotAccountsModule.fetchKioskPrinters,

    // Startup Media (from sotAccounts module)
    uploadStartupMedia: sotAccountsModule.uploadStartupMedia,
    deleteStartupMedia: sotAccountsModule.deleteStartupMedia,
    // SOT Account API operations
    suggestSOTAccountName: sotAccountsModule.suggestAccountName,
    fetchSOTAccountDetails: sotAccountsModule.fetchSOTAccountDetails,
    updateSOTAccountAPI: sotAccountsModule.updateSOTAccountAPI,

    // Service Areas (from module)
    loadServiceAreas: serviceAreasModule.loadServiceAreas,
    loadServiceAreaSettings: serviceAreasModule.loadServiceAreaSettings,
    loadServiceAreaReceptionAccounts: receptionAccountsModule.loadReceptionAccounts,
    getRestaurantServiceAreas: serviceAreasModule.getRestaurantServiceAreas,
    getServiceAreaById: serviceAreasModule.getServiceAreaById,
    createServiceArea: serviceAreasModule.createServiceArea,
    updateServiceArea: serviceAreasModule.updateServiceArea,
    deleteServiceArea: serviceAreasModule.deleteServiceArea,
    saveServiceAreaSettings: serviceAreasModule.saveServiceAreaSettings,
    getServiceAreaReceptionAccounts: () => serviceAreasModule.receptionAccounts.value,
    initializeServiceAreaDraft: serviceAreasModule.initializeDraft,
    updateServiceAreaDraft: serviceAreasModule.updateDraft,
    getServiceAreaDraft: serviceAreasModule.getDraft,

    // Articles (from module)
    addArticle: articlesModule.addArticle,
    updateArticle: articlesModule.updateArticle,
    deleteArticle: articlesModule.deleteArticle,
    getArticle: articlesModule.getArticle,
    getRestaurantArticles: articlesModule.getRestaurantArticles,
    toggleFrozen: articlesModule.toggleFrozen,
    copyArticle: articlesModule.copyArticle,
    moveArticlePosition: articlesModule.moveArticlePosition,
    reorderArticles: articlesModule.reorderArticles,
    reorderArticlesInSubcategory: articlesModule.reorderArticlesInSubcategory,
    saveArticlesOrder: articlesModule.saveArticlesOrder,
    bulkFreezeArticles: (restaurantId: string, ids: string[], freeze: boolean) =>
      bulkEditModule.bulkFreezeArticles(restaurantId, ids, freeze, apiInstance),
    bulkDigitalMenuArticles: (restaurantId: string, ids: string[], activate: boolean) =>
      bulkEditModule.bulkDigitalMenuArticles(restaurantId, ids, activate, apiInstance),
    bulkUpdateArticles: (restaurantId: string, payload: any) =>
      bulkEditModule.bulkUpdateArticles(restaurantId, payload, apiInstance),
    toggleFrozenArticle: (restaurantId: string, id: string, isFrozen: number, pinNumber?: string) =>
      articlesModule.toggleFrozen(restaurantId, id, isFrozen, apiInstance, pinNumber),
    toggleDigitalMenuArticle: (restaurantId: string, id: string, currentValue: number) =>
      articlesModule.toggleDigitalMenu(restaurantId, id, currentValue, apiInstance),
    initializeArticleDraft: articlesModule.initializeDraft,
    updateArticleDraft: articlesModule.updateDraft,
    getArticleDraft: articlesModule.getDraft,
    clearArticleDraft: articlesModule.clearDraft,
    toggleArticleSelection: bulkEditModule.toggleSelection,
    selectAllArticles: bulkEditModule.selectAll,
    deselectAllArticles: bulkEditModule.deselectAll,
    isArticleSelected: bulkEditModule.isSelected,
    getSelectedArticlesCount: bulkEditModule.getSelectedCount,
    getSelectedArticles: bulkEditModule.getSelected,
    loadMenuItemsFromAPI: async (id: string) => {
      await articlesModule.loadMenuItemsFromAPI(id, apiInstance)
    },
    createMenuItem: async (restaurantId: string, payload: any) => {
      return await articlesModule.createMenuItem(restaurantId, payload, apiInstance)
    },
    updateMenuItem: async (restaurantId: string, itemId: string, payload: any) => {
      return await articlesModule.updateMenuItem(restaurantId, itemId, payload, apiInstance)
    },
    getMenuItemById: async (restaurantId: string, itemId: string) => {
      return await articlesModule.getMenuItemById(restaurantId, itemId, apiInstance)
    },

    // Menu Languages (from module)
    fetchMenuLanguages: menuLanguagesModule.fetchMenuLanguages,
    updateMenuLanguageSelection: menuLanguagesModule.updateLanguageSelection,
    getSelectedMenuLanguages: menuLanguagesModule.getSelectedLanguages,
    getAvailableMenuLanguageCodes: menuLanguagesModule.getAvailableLanguageCodes,
    isMenuLanguageDefault: menuLanguagesModule.isLanguageDefault,
    isMenuLanguageSelected: menuLanguagesModule.isLanguageSelected,

    // Restaurant Price Levels (from module)
    updateRestaurantPriceLevels: priceLevelsModule.updateLevels,
    getRestaurantPriceLevels: priceLevelsModule.getLevels,
    addRestaurantPriceLevel: priceLevelsModule.addLevel,
    updateRestaurantPriceLevel: priceLevelsModule.updateLevel,
    deleteRestaurantPriceLevel: priceLevelsModule.deleteLevel,

    // Offering Time Presets (from module)
    addOfferingTimePreset: offeringTimePresetsModule.addPreset,
    getOfferingTimePresetsForRestaurant: offeringTimePresetsModule.getPresetsForRestaurant,
    getOfferingTimePresetById: offeringTimePresetsModule.getPresetById,
    updateOfferingTimePreset: offeringTimePresetsModule.updatePreset,
    deleteOfferingTimePreset: offeringTimePresetsModule.deletePreset,
    loadOfferingTimePresets: offeringTimePresetsModule.loadPresetsForRestaurant,

    // Cloud Printers (from module)
    cloudPrinters,
    cloudPrintersLoading,
    cloudPrintersError,
    fetchCloudPrinters: cloudPrintersModule.fetchPrinters,
    deleteCloudPrinter: cloudPrintersModule.deletePrinter,
    updateCloudPrinter: cloudPrintersModule.updatePrinter,
    createCloudPrinter: cloudPrintersModule.createPrinter,
    fetchCloudPrinterCategories: cloudPrintersModule.fetchCategories,

    // Menu Categories (from module)
    menuCategories,
    menuCategoriesLoading: menuCategoriesModule.isLoading,
    menuCategoriesError: menuCategoriesModule.error,
    fetchMenuCategories: menuCategoriesModule.fetchMenuCategories,
    uploadCategoryMedia: menuCategoriesModule.uploadMedia,
    deleteCategoryMedia: menuCategoriesModule.deleteMedia,
    createCategoryAPI: menuCategoriesModule.createCategoryAPI,
    editCategoryAPI: menuCategoriesModule.editCategoryAPI,
    deleteCategoryAPI: menuCategoriesModule.deleteCategoryAPI,
    addMenuCategory: menuCategoriesModule.addCategory,
    updateMenuCategory: menuCategoriesModule.updateCategory,
    deleteMenuCategory: menuCategoriesModule.deleteCategory,
    findMenuCategoryById: menuCategoriesModule.findCategoryById,
    findMenuCategoryParents: menuCategoriesModule.findCategoryParents,
    reorderMenuCategories: menuCategoriesModule.reorderCategories,
    reorderMenuSubcategories: menuCategoriesModule.reorderSubcategories,

    // Food Categories (from module)
    foodCategories,
    fetchFoodCategories: foodCategoriesModule.fetchFoodCategories,
    saveFoodCategory: foodCategoriesModule.saveCategory,
    createFoodCategory: foodCategoriesModule.createCategory,
    updateFoodCategory: foodCategoriesModule.updateCategory,
    deleteFoodCategory: foodCategoriesModule.deleteCategory,
    isFoodCategoriesLoading: foodCategoriesModule.isLoading,

    // Food Attributes (from module)
    foodAttributesProperties,
    foodAttributesAllergens,
    foodAttributesAdditives,
    fetchFoodAttributes: foodAttributesModule.fetchFoodAttributes,

    // Food Item Options (from module)
    foodItemOptions,
    fetchFoodItemOptions: foodItemOptionsModule.fetchFoodItemOptions,
    uploadOptionGroupMedia: foodItemOptionsModule.uploadMedia,
    deleteOptionGroupMedia: foodItemOptionsModule.deleteMedia,
    createOptionGroup: foodItemOptionsModule.createOptionGroup,
    updateOptionGroup: foodItemOptionsModule.updateOptionGroup,
    sortOptionGroups: foodItemOptionsModule.sortOptionGroups,
    resetFoodItemOptions: foodItemOptionsModule.reset,

    // Orders (Orders List - from module)
    orders,
    ordersErrorSummary,
    currentPage,
    lastPage,
    total,
    perPage,
    isLoading: isOrdersLoading,
    error: ordersError,
    sortBy: ordersSortBy,
    sortDir: ordersSortDir,
    filters: ordersFilters,
    fetchOrders: ordersModule.fetchOrders,
    goToPage: ordersModule.goToPage,
    changePerPage: ordersModule.changePerPage,
    setSortBy: ordersModule.setSortBy,
    applyFilters: ordersModule.applyFilters,
    resetFilters: ordersModule.resetFilters,
    getOrderById: ordersModule.getOrderById,
    hasOrders: ordersModule.hasOrders,
    hasNextPage: ordersModule.hasNextPage,
    hasPreviousPage: ordersModule.hasPreviousPage,
    isFirstPage: ordersModule.isFirstPage,
    isLastPage: ordersModule.isLastPage,

    // Modules (for direct access)
    priceLevelsModule,
    menuLanguagesModule,
    foodCategoriesModule,
    foodAttributesModule,
    foodItemOptionsModule,

    // Deprecated
    updateData,
    loadRestaurantInfo,
  }
})
